import { FormInstance } from "antd";
import { defaultCategoryRepository } from "api/repositories/categories/CategoriesRepository";
import { CategoriesResponseModel } from "api/repositories/categories/model/CategoriesResponseModel";
import { defaultFieldsRepository } from "api/repositories/fields/FieldsRepository";
import { FieldsResponseModel } from "api/repositories/fields/model/FieldsResponseModel";
import { defaultGoodsRepository } from "api/repositories/goods/GoodsRepository";
import { GoodsResponseModel } from "api/repositories/goods/model/GoodsResponseModel";
import { defaultInventoriesRepository } from "api/repositories/inventories/InventoriesRepository";
import { InventoryResponseModel } from "api/repositories/inventories/model/InventoryResponseModel";
import { PartnerResponseModel } from "api/repositories/partners/model/PartnerResponseModel";
import { defaultPartnerManagement } from "api/repositories/partners/PartnerRepository";
import { defaultRoleRepository } from "api/repositories/roles/RolesRepository";
import { StoragesResponseModel } from "api/repositories/storages/model/StoragesResponseModel";
import { defaultStoragesRepository } from "api/repositories/storages/StoragesRepository";
import { UserFeatureResponseModel } from "api/repositories/users/model/feature/UserFeatureResponseModel";
import { UsersResponseModel } from "api/repositories/users/model/UsersResponseModel";
import { defaultUserRepository } from "api/repositories/users/UsersRepository";
import { VariantsResponseModel } from "api/repositories/variants/model/VariantsResponseModel";
import { defaultVariantsRepository } from "api/repositories/variants/VariantsRepository";
import { WalletsResponseModel } from "api/repositories/wallets/model/WalletsResponseModel";
import { defaultWalletsRepository } from "api/repositories/wallets/WalletsRepository";
import { SidebarConfigModel } from "api/sidebarConfig/model/SidebarConfigModel";
import SidebarConfigRepository from "api/sidebarConfig/SidebarConfigRepository";
import { useFilterListCache } from "hooks/useFilterListCache";
import ls from "localstorage-slim";
import { isNull } from "lodash";
import moment from "moment";
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import { getMessage } from "utils/helpers";
import { strings } from "utils/localizeStrings";
import GlobalVariablesList from "./GlobalVariablesList";

export interface AuthenticationProps {
  onLogin: any;
  onLogout: any;
  token: string;
  user: UsersResponseModel;
  profile: any;
  onSignUp: any;
  isAuthenticated: () => boolean;
  loading: boolean;
  menuConfig: SidebarConfigModel;
  variantsForFilter: VariantsResponseModel[];
  suppliersForFilter: PartnerResponseModel[];
  categoriesForFilter: CategoriesResponseModel[];
  storagesForFilter: StoragesResponseModel[];
  goodsForFilter: GoodsResponseModel[];
  inventoriesForFilter: InventoryResponseModel[];
  fieldsForFilters: FieldsResponseModel[];
  userFeature: UserFeatureResponseModel[];
  walletForFilter: WalletsResponseModel[];
  userRole: string;
  globalFilterForm: FormInstance<any>;
  globalActionForm: FormInstance<any>;
  globalImportForm: FormInstance<any>;
  importRequestForm: FormInstance<any>;
  authReady: boolean;
  globalModal: {
    supplierImport: boolean;
  };
  setGlobalModal: Dispatch<
    SetStateAction<{
      supplierImport: boolean;
    }>
  >;
  excelLoading: {
    transaction: boolean;
    transactionWarehouse: boolean;
    generals: boolean;
    items: boolean;
    import: boolean;
    export: boolean;
    supplierImport: boolean;
    mSaleImport: boolean;
  };
  globalTemp: unknown;
  setGlobalTemp: Dispatch<SetStateAction<unknown>>;
  stepProps: Record<string, boolean>;
  setStepProps: Dispatch<SetStateAction<Record<string, boolean>>>;
  activeImportStepProps: Record<string, boolean>;
  breadCrumb: Array<{
    href: string;
    title: ReactNode | string;
  }>;
  setBreadCrumb: Dispatch<
    SetStateAction<
      Array<{
        href: string;
        title: ReactNode | string;
      }>
    >
  >;
  setActiveImportStepProps: Dispatch<SetStateAction<Record<string, boolean>>>;
  setExcelLoading: Dispatch<
    SetStateAction<{
      transaction: boolean;
      transactionWarehouse: boolean;
      generals: boolean;
      items: boolean;
      import: boolean;
      export: boolean;
      supplierImport: boolean;
      mSaleImport: boolean;
    }>
  >;
  autoLogout: (errorCode: string) => void;
  fetchUserFeature: () => Promise<void>;
  setWalletForFilter: Dispatch<SetStateAction<WalletsResponseModel[]>>;
  setUserRole: Dispatch<SetStateAction<string>>;
  setUserFeature: Dispatch<SetStateAction<UserFeatureResponseModel[]>>;
  setFieldsForFilters: Dispatch<SetStateAction<FieldsResponseModel[]>>;
  setVariantsForFilter: Dispatch<SetStateAction<VariantsResponseModel[]>>;
  setSuppliersForFilter: Dispatch<SetStateAction<PartnerResponseModel[]>>;
  setCategoriesForFilter: Dispatch<SetStateAction<CategoriesResponseModel[]>>;
  setStoragesForFilter: Dispatch<SetStateAction<StoragesResponseModel[]>>;
  setGoodsForFilter: Dispatch<SetStateAction<GoodsResponseModel[]>>;
  setInventoriesForFilter: Dispatch<SetStateAction<InventoryResponseModel[]>>;
  setAuthReady: Dispatch<SetStateAction<boolean>>;
  fetchFilterList: <T extends Object, P extends Object>(
    dataState: Dispatch<SetStateAction<T[]>>,
    type:
      | "variant"
      | "supplier"
      | "category"
      | "storage"
      | "goods"
      | "inventory"
      | "field"
      | "wallet"
      | "partner"
      | "role"
      | "users",
    params?: P,
    isForceRefresh?: boolean
  ) => Promise<void>;
}

export const AuthContext = createContext({});

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const filterListCache = useFilterListCache;
  ls.config.encrypt = true;
  const {
    categoriesForFilter,
    excelLoading,
    fieldsForFilters,
    globalActionForm,
    globalFilterForm,
    globalImportForm,
    globalModal,
    goodsForFilter,
    inventoriesForFilter,
    loading,
    menuConfig,
    profile,
    storagesForFilter,
    suppliersForFilter,
    token,
    user,
    userFeature,
    userRole,
    variantsForFilter,
    walletForFilter,
    stepProps,
    importRequestForm,
    activeImportStepProps,
    breadCrumb,
    globalTemp,
    authReady,
    setAuthReady,
    setGlobalTemp,
    setBreadCrumb,
    setActiveImportStepProps,
    setStepProps,
    setCategoriesForFilter,
    setExcelLoading,
    setFieldsForFilters,
    setGlobalModal,
    setGoodsForFilter,
    setInventoriesForFilter,
    setMenuConfig,
    setStoragesForFilter,
    setSuppliersForFilter,
    setToken,
    setUser,
    setUserFeature,
    setUserRole,
    setVariantsForFilter,
    setWalletForFilter,
  } = GlobalVariablesList();
  const fetchFilterList = async <T extends Object, P extends Object>(
    dataState: Dispatch<SetStateAction<T[]>>,
    type:
      | "variant"
      | "supplier"
      | "category"
      | "storage"
      | "goods"
      | "inventory"
      | "wallet"
      | "field"
      | "partner"
      | "role"
      | "users",
    params?: P,
    isForceRefresh: boolean = false
  ) => {
    try {
      let res = undefined;
      if (!isNull(filterListCache.getFilterCache(type)) && !isForceRefresh) {
        dataState(filterListCache.getFilterCache(type));
        return filterListCache.getFilterCache(type);
      } else {
        switch (type) {
          case "variant":
            res = await defaultVariantsRepository?.getList(
              !params ? { pagination: false } : params
            );
            if (res) {
              !isForceRefresh &&
                filterListCache.setFilterCache(type, res?.data);
              dataState(res?.data);
            }
            break;
          case "supplier":
            res = await defaultPartnerManagement?.getList(
              !params ? { pagination: false, type: "1" } : params
            );
            if (res) {
              !isForceRefresh &&
                filterListCache.setFilterCache(type, res?.data);
              dataState(res?.data);
            }
            break;
          case "category":
            res = await defaultCategoryRepository?.getList(
              !params ? { pagination: false } : params
            );
            if (res) {
              !isForceRefresh &&
                filterListCache.setFilterCache(type, res?.data);
              dataState(res?.data);
            }
            break;
          case "storage":
            res = await defaultStoragesRepository?.getList(
              !params ? { pagination: false } : params
            );
            if (res) {
              !isForceRefresh &&
                filterListCache.setFilterCache(type, res?.data);
              dataState(res?.data);
            }
            break;
          case "goods":
            res = await defaultGoodsRepository?.getList(
              !params ? { pagination: false } : params
            );
            if (res) {
              !isForceRefresh &&
                filterListCache.setFilterCache(type, res?.data);
              dataState(res?.data);
            }
            break;
          case "partner":
            res = await defaultPartnerManagement?.getList(
              !params ? { pagination: false, type: "0" } : params
            );
            if (res) {
              !isForceRefresh &&
                filterListCache.setFilterCache(type, res?.data);
              dataState(res?.data);
            }
            break;
          case "inventory":
            res = await defaultInventoriesRepository?.getList(
              !params ? { pagination: false } : params
            );
            if (res) {
              !isForceRefresh &&
                filterListCache.setFilterCache(type, res?.data);
              dataState(res?.data);
            }
            break;
          case "field":
            res = await defaultFieldsRepository?.getList(
              !params ? { pagination: false } : params
            );
            if (res) {
              !isForceRefresh &&
                filterListCache.setFilterCache(type, res?.data);
              dataState(res?.data);
            }
            break;
          case "wallet":
            res = await defaultWalletsRepository?.getList(
              !params ? { pagination: false } : params
            );
            if (res) {
              !isForceRefresh &&
                filterListCache.setFilterCache(type, res?.data);
              dataState(res?.data);
            }
            break;
          case "role":
            res = await defaultRoleRepository.getList(
              !params ? { pagination: false } : params
            );
            if (res) {
              !isForceRefresh &&
                filterListCache.setFilterCache(type, res?.data);
              dataState(res?.data);
            }
            break;
          case "users":
            res = await defaultUserRepository.getList(
              !params ? { pagination: false } : params
            );
            if (res) {
              !isForceRefresh &&
                filterListCache.setFilterCache(type, res?.data);
              dataState(res?.data);
            }
            break;
        }
        return res?.data;
      }
    } catch (error) {
      if (error?.code === "N0126") {
        autoLogout(error?.code);
      }
      console.log(error);
    }
  };

  const getSidebar = useCallback(
    async (feature: typeof userFeature) => {
      try {
        const siderConfig = await SidebarConfigRepository?.getSidebarMenu(
          feature
        );
        setMenuConfig(siderConfig?.data);
      } catch (error) {
        console.log(error);
      }
    },
    [navigate]
  );

  const fetchUserFeature = async () => {
    try {
      const res = await defaultUserRepository?.getFeature();
      if (res) {
        setUserFeature(res?.data);
        ls.set("warehouse-feature", JSON.stringify(res?.data));
        await getSidebar(res?.data);
      }
    } catch (error) {
      if (error?.code === "N0126") {
        autoLogout(error?.code);
      }
      console.log(error);
    }
  };

  const onLogin = async (user: UsersResponseModel, token: string) => {
    localStorage.setItem("warehouse-cms-token", token);
    ls.set("lastLogin", moment().format("DD/MM/YYYY"));
    setUserRole(user?.role?.code);
    ls.set("user", JSON.stringify(user));
    setUser(user);
    await fetchUserFeature();
    setToken(token);
    navigate("/history/transactions-history");
  };

  useEffect(() => {
    const token = localStorage.getItem("warehouse-cms-token");
    let user = JSON.parse(ls.get("user")!);
    let features = JSON.parse(ls.get("warehouse-feature")!);
    let date = ls.get("lastLogin");
    if (
      !token ||
      !user ||
      !features ||
      moment().format("DD/MM/YYYY") !== date
    ) {
      onLogout();
      setAuthReady(true);
      return;
    }
    if (!!token && !!features && !!user) {
      setUserFeature(features);
      getSidebar(features);
      setToken(token);
      setUser(user);
      setAuthReady(true);
      setUserRole(user?.role?.code);
    }
  }, []);

  const onSignUp = async (user: UsersResponseModel) => {
    localStorage.setItem("user", JSON.stringify(user));
    setUser(user);
    navigate("/sign-in");
  };

  const autoLogout = (errorCode: string) => {
    if (errorCode === "N0126") {
      getMessage(`${strings.GlobalMessage.TokenExpired}`, 4, "warning");
      onLogout();
    }
  };

  const onLogout = () => {
    localStorage.clear();
    setToken(undefined);
    setUser(undefined);
    navigate("/sign-in");
  };

  const isAuthenticated = () => {
    return !!token && !!user;
  };

  const authValue = useMemo(
    () => ({
      loading,
      profile,
      user,
      token,
      menuConfig,
      variantsForFilter,
      suppliersForFilter,
      categoriesForFilter,
      storagesForFilter,
      goodsForFilter,
      inventoriesForFilter,
      fieldsForFilters,
      userFeature,
      userRole,
      walletForFilter,
      excelLoading,
      globalModal,
      globalFilterForm,
      globalActionForm,
      globalImportForm,
      stepProps,
      importRequestForm,
      activeImportStepProps,
      breadCrumb,
      globalTemp,
      authReady,
      setAuthReady,
      autoLogout,
      setGlobalTemp,
      setBreadCrumb,
      setActiveImportStepProps,
      setStepProps,
      setGlobalModal,
      setExcelLoading,
      setWalletForFilter,
      setUserFeature,
      setUserRole,
      setFieldsForFilters,
      onLogout,
      isAuthenticated,
      onLogin,
      onSignUp,
      setVariantsForFilter,
      setSuppliersForFilter,
      setCategoriesForFilter,
      setStoragesForFilter,
      setGoodsForFilter,
      setInventoriesForFilter,
      fetchFilterList,
      fetchUserFeature,
    }),
    [
      loading,
      profile,
      user,
      token,
      menuConfig,
      variantsForFilter,
      suppliersForFilter,
      categoriesForFilter,
      storagesForFilter,
      goodsForFilter,
      inventoriesForFilter,
      fieldsForFilters,
      userFeature,
      userRole,
      walletForFilter,
      excelLoading,
      globalModal,
      stepProps,
      activeImportStepProps,
      breadCrumb,
      globalTemp,
      authReady,
    ]
  );

  return (
    <AuthContext.Provider value={authValue}>{children}</AuthContext.Provider>
  );
};

export const useAuth = (): AuthenticationProps =>
  useContext(AuthContext) as AuthenticationProps;
